import { useState } from 'react';
import { PreviewGrid } from '../../../../components/PreviewGrid';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { Skeleton } from '../../../../components/Skeleton';
import { TextCell } from '../../../../components/table/TextCell';
import {
  APPLICANT_ASSESSMENT_RESULT,
  type ApplicantAssessment,
  type ApplicantAssessmentStageValue,
  type ApplicantScoreWeights,
} from '../applicantTypes';
import { ScoreWeightsModal } from './ScoreWeightsModal';

/** One line of the Summary. `score` null = not recorded; `hidden` = a health stage this reader may not see. */
interface SummaryLine {
  stage: ApplicantAssessmentStageValue;
  label: string;
  resultLabel: string | null;
  hidden: boolean;
  weight: number | null;
  score: number | null;
}

/**
 * The SCORE as the product owner defined it (20 Oct 2026): **the weight IS the points** — a Passed stage
 * earns its weight, a Failed one 0, an unrecorded one nothing yet. The total is out of 100.
 *
 * **A HIDDEN stage hides the TOTAL too**: the server sends no result for it, and a total over the stages
 * the reader CAN see would let them subtract their way to the one they cannot. ONE derivation, so the
 * rows and the footer cannot disagree.
 */
function summaryLines(assessments: ApplicantAssessment[], weights: ApplicantScoreWeights): SummaryLine[] {
  return assessments.map((a) => {
    const weight = weights.weights.find((w) => w.stage === a.stage)?.weight ?? null;
    const score =
      a.isHidden || a.result == null || weight == null ? null : a.result === APPLICANT_ASSESSMENT_RESULT.Passed ? weight : 0;

    return { stage: a.stage, label: a.stageLabel, resultLabel: a.resultLabel, hidden: a.isHidden, weight, score };
  });
}

/** Two decimals always — a weight is stored to two — and in the mono face, as every figure. */
function points(value: number): string {
  return value.toFixed(2);
}

/**
 * The applicant's **Summary** tab — one row per assessment stage: its result, its weight in Score
 * Points, and the points it earned; the total below. A DERIVED VIEW of fields every stage card edits;
 * its one Edit is the Score Points form — the scoring rule, not the stages — offered as the empty
 * banner's button while no weights are set (product call, 20 Oct 2026).
 */
export function ApplicantSummaryPanel({
  assessments,
  weights,
  failed,
  onWeightsSaved,
}: {
  /** Null while loading. */
  assessments: ApplicantAssessment[] | null;
  weights: ApplicantScoreWeights | null;
  /** Either feed failed — the card says so rather than scoring against nothing. */
  failed: boolean;
  /** The weights as just saved — the Summary re-scores at once, with no refetch. */
  onWeightsSaved: (next: ApplicantScoreWeights) => void;
}) {
  const [editingWeights, setEditingWeights] = useState(false);

  if (failed) {
    return (
      <ProfileCard id="applicant-profile-summary" title="Summary" padded>
        <p className="text-sm text-slate-500">The summary could not be loaded. Refresh the page to try again.</p>
      </ProfileCard>
    );
  }

  if (!assessments || !weights) {
    return (
      <ProfileCard id="applicant-profile-summary" title="Summary" padded>
        <Skeleton className="h-40 w-full" />
      </ProfileCard>
    );
  }

  const lines = summaryLines(assessments, weights);
  const anyHidden = lines.some((l) => l.hidden);
  const total = lines.reduce((sum, l) => sum + (l.score ?? 0), 0);
  const outOf = lines.reduce((sum, l) => sum + (l.weight ?? 0), 0);

  return (
    <>
      <ProfileCard
        id="applicant-profile-summary"
        title="Summary"
        // The grid draws its OWN rounded border, so it sits inset.
        padded
        /*
          A SHORTCUT to Score Points (product call, 20 Oct 2026): the same form Settings opens, under the
          same key, so the empty banner's button sets the weights where their absence is noticed. The
          cog offers it after, as "Edit score points". Hidden without `configure-weights`.
        */
        onEdit={() => setEditingWeights(true)}
        editPermission="applicants.configure-weights"
        editLabel="Edit score points"
        empty={
          weights.isConfigured
            ? undefined
            : {
                title: 'Score points are not set.',
                description:
                  'Each stage needs a weight before the Summary can score it. They are set under Recruitment › Settings › Score Points.',
                actionLabel: 'Set score points',
              }
        }
      >
        <PreviewGrid<SummaryLine>
          rows={lines}
          rowKey={(l) => String(l.stage)}
          empty="No assessment stages."
          columns={[
            { header: 'Stage', width: 240, render: (l) => <TextCell value={l.label} /> },
            {
              header: 'Result',
              width: 140,
              render: (l) => <TextCell value={l.hidden ? 'Hidden' : l.resultLabel} />,
              footer: () => <span className="text-slate-600">Total</span>,
            },
            {
              header: 'Weight (%)',
              width: 120,
              align: 'right',
              render: (l) => <TextCell mono numeric value={l.weight != null ? points(l.weight) : null} />,
              footer: () => <span className="font-mono tabular-nums text-slate-600">{points(outOf)}</span>,
            },
            {
              header: 'Score',
              width: 120,
              align: 'right',
              // "Hidden" is a word, not a figure — it takes the body face.
              render: (l) =>
                l.hidden ? <TextCell value="Hidden" /> : <TextCell mono numeric value={l.score != null ? points(l.score) : null} />,
              footer: () =>
                anyHidden ? (
                  <span className="text-slate-600">Hidden</span>
                ) : (
                  <span className="font-mono tabular-nums text-slate-600">{points(total)}</span>
                ),
            },
          ]}
        />
      </ProfileCard>

      {editingWeights ? (
        <ScoreWeightsModal
          current={weights}
          onClose={() => setEditingWeights(false)}
          onSaved={(next) => {
            setEditingWeights(false);
            onWeightsSaved(next);
          }}
        />
      ) : null}
    </>
  );
}
